import React, { useEffect, useState } from 'react';
import { FlaskConical, X, RefreshCw, Check } from 'lucide-react';
import axios from 'axios';
import { getCrmOverride, clearCrmOverride } from '../utils/envOverride';

const LS_SEED_TOKEN = 'wd_staging_seed_token';
const LS_TEST_PHONE = 'wd_staging_test_phone';
const DEFAULT_TEST_PHONE = '+919999999999';

/**
 * Tiny pinned badge that shows when the website is pointing to a non-prod
 * CRM env (staging / custom). Clicking "×" clears the override and falls
 * back to the production env vars baked at build time.
 *
 * On staging the badge also exposes a "Reset OTP" dev tool that calls the
 * CRM's token-gated /api/admin/clear-otp-limit endpoint so back-to-back UAT
 * runs don't hit the 5/hr/phone OTP send cap. The seed token is stored in
 * localStorage (staging only, cleared alongside the env override).
 */
export default function EnvBadge() {
  const [override, setOverride] = useState(() => getCrmOverride());
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(''); // 'ok' | 'err' | ''

  useEffect(() => {
    const refresh = () => setOverride(getCrmOverride());
    window.addEventListener('wd-crm-env-changed', refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener('wd-crm-env-changed', refresh);
      window.removeEventListener('storage', refresh);
    };
  }, []);

  if (!override) return null;

  const label = (override.env || 'custom').toUpperCase();
  const host = (() => {
    try {
      return new URL(override.url).host;
    } catch {
      return override.url;
    }
  })();

  const isStaging = label === 'STAGING';
  const bg = isStaging ? 'bg-amber-500' : 'bg-fuchsia-600';

  const handleResetOtp = async () => {
    let token = localStorage.getItem(LS_SEED_TOKEN) || '';
    let phone = localStorage.getItem(LS_TEST_PHONE) || DEFAULT_TEST_PHONE;

    if (!token) {
      const t = window.prompt(
        'Paste the staging seed token (from Kalyan). Stored locally — never sent to prod.',
      );
      if (!t) return;
      token = t.trim();
      localStorage.setItem(LS_SEED_TOKEN, token);
    }

    const p = window.prompt('Phone to reset (E.164):', phone);
    if (!p) return;
    phone = p.trim();
    localStorage.setItem(LS_TEST_PHONE, phone);

    setBusy(true);
    setResult('');
    try {
      await axios.post(
        `${override.url}/api/admin/clear-otp-limit`,
        null,
        {
          params: { phone },
          headers: { 'x-seed-token': token },
          timeout: 10000,
        },
      );
      setResult('ok');
    } catch (e) {
      // If token got rejected, drop the cached token so next click re-prompts
      if (e?.response?.status === 401 || e?.response?.status === 403) {
        localStorage.removeItem(LS_SEED_TOKEN);
      }
      setResult('err');
    } finally {
      setBusy(false);
      setTimeout(() => setResult(''), 2000);
    }
  };

  return (
    <div
      data-testid="env-badge"
      className={`fixed bottom-4 left-4 z-[70] flex items-center gap-1 ${bg} text-white text-xs font-semibold rounded-full shadow-lg pl-3 pr-1.5 py-1.5`}
      title={`CRM: ${override.url}`}
    >
      <FlaskConical className="h-3.5 w-3.5" />
      <span className="tracking-wide">{label}</span>
      <span className="text-white/70 hidden sm:inline font-normal ml-1">· {host}</span>

      {isStaging && (
        <button
          type="button"
          onClick={handleResetOtp}
          disabled={busy}
          title="Reset OTP rate-limit counter (staging only)"
          className="ml-2 h-6 w-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition disabled:opacity-50"
          aria-label="Reset OTP counter"
          data-testid="env-badge-reset-otp"
        >
          {busy ? (
            <RefreshCw className="h-3.5 w-3.5 animate-spin" />
          ) : result === 'ok' ? (
            <Check className="h-3.5 w-3.5 text-emerald-300" strokeWidth={3} />
          ) : result === 'err' ? (
            <X className="h-3.5 w-3.5 text-red-300" strokeWidth={3} />
          ) : (
            <RefreshCw className="h-3.5 w-3.5" />
          )}
        </button>
      )}

      <button
        type="button"
        onClick={() => {
          localStorage.removeItem(LS_SEED_TOKEN);
          localStorage.removeItem(LS_TEST_PHONE);
          clearCrmOverride();
          window.location.reload();
        }}
        className="ml-1 h-6 w-6 rounded-full bg-white/20 hover:bg-white/30 flex items-center justify-center transition"
        aria-label="Reset to production CRM"
        data-testid="env-badge-reset"
      >
        <X className="h-3.5 w-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
